/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import {
  Animation,
  Heading,
  HeadingLevel,
  Link,
  Spacer,
  Text,
  Typography,
} from '@zapier/design-system';

import { useUniversalNavContext } from '../../../../hooks/useUniversalNavContext';
import type { PromoLinksListType } from '../../../../constants';
import { emitUserInteractionEvent } from '../../../../utils/avroEvents';
import { ArrowLinkIcon } from '../..';

type Props = PromoLinksListType & {
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
};

const Styles = {
  root: css({
    color: 'var(--zds-gray-warm-10, #201515)',
    font: `var(--zds-small-print-4, 700 13px/20px ${Typography.base.fontFamily})`,
  }),
  listItem: css({
    marginBottom: 8,
    '&:last-of-type': {
      marginBottom: 0,
    },
  }),
  link: css({
    font: `var(--zds-small-print-1, 400 13px/20px ${Typography.base.fontFamily})`,
    color: 'var(--zds-gray-warm-10, #201515)',
    transition: `text-decoration 100ms ${Animation.transitionTimingFunction}`,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
      color: 'var(--zds-gray-warm-10, #201515)',
    },
    '&:hover > span': {
      marginLeft: 4,
      opacity: 1,
    },
    '&:focus': {
      outline: `1px solid var(--zds-ui-primary-strongest, #503EBD)`,
      color: 'var(--zds-gray-warm-10, #201515)',
    },
  }),
};

/**
 * A list of promotional links in the MegaNav
 */
export const ListPromo = (props: Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const handleEvent = (text: string, eventType: string) => {
    emitUserInteractionEvent({
      event_action: eventType,
      option_selected: `ListPromo: ${text}`,
    });
    if (eventType === 'click' && props.onCloseMenu) props.onCloseMenu();
  };

  return (
    <section css={Styles.root}>
      {props.text && (
        <HeadingLevel>
          <Heading>
            <Text type="smallPrint1Bold">{props.text}</Text>
          </Heading>
          <Spacer height={20} />
        </HeadingLevel>
      )}
      <ul data-zds>
        {props.links.map((link) => (
          <li key={link.text} css={Styles.listItem} data-zds>
            <Link
              color={null}
              css={Styles.link}
              href={getFullyQualifiedURL(link.href) || undefined}
              onClick={() => handleEvent(link.text, 'click')}
              onMouseEnter={() => handleEvent(link.text, 'hover')}
              target={link.target}
            >
              {link.text}
              <ArrowLinkIcon />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};
